import React from 'react';

import {
  Currency,
  GiftcardActivationOrder,
  GiftcardOrder,
  GiftcardProduct,
  RedeemOrder,
} from '../../types/apiTypes';
import { AdjustmentType } from '../../types/common';
import {
  AffiliationDataLayerParam,
  DataLayerCheckoutProduct,
  DataLayerClickProduct,
  DataLayerProduct,
  EventTypes,
} from '../../types/dataLayer';
import { serverUtils } from '../../utils/serverUtils/serverUtils';
import { dataLayerMappingHooks } from '../dataLayerMappingHooks/dataLayerMappingHooks';

export type AddToCartParams = {
  currencyCode: Currency;
  isRedeem?: boolean;
  products: DataLayerCheckoutProduct[];
};
export type RemoveFromCartParams = {
  currencyCode: Currency;
  isRedeem?: boolean;
  products: DataLayerCheckoutProduct[];
};
export type CheckoutParams = {
  isRedeem?: boolean;
  paymentMethodId?: string;
  products: DataLayerCheckoutProduct[];
};
export type SuccessfulOrderParams = {
  billingEmail?: string | null;
  id: string;
  isRedeem?: boolean;
  products: DataLayerCheckoutProduct[];
  revenue: string;
  shipping: string;
  tax: string;
};

export type TrackingSuccessfulOrderArgs = {
  isRedeem?: boolean;
  order?: GiftcardOrder | GiftcardActivationOrder | null;
  redeemOrder?: RedeemOrder | null;
};

export const dataLayerHooks = {
  useSearchInputCallback(searchText: string | null) {
    return React.useCallback((searchResults: GiftcardProduct[] | null) => {
      if (searchText && searchText?.length > 0 && searchResults !== null) {
        window.dataLayer.push({
          event: EventTypes.search,
          search_term: searchText,
          count_of_search_results: searchResults.length,
        });
      }
    }, [searchText]);
  },

  useProductClickCallback(product: DataLayerClickProduct, isRedeem?: boolean) {
    return React.useCallback(() => {
      if (isRedeem) {
        window.dataLayer.push({
          event: EventTypes.productClick,
          ecommerce: {
            click: {
              actionField: { checkout: 'Redeem' },
              products: [product],
            },
          },
        });
      } else {
        window.dataLayer.push({
          event: EventTypes.productClick,
          ecommerce: {
            click: {
              products: [product],
            },
          },
        });
      }
    }, [product, isRedeem]);
  },

  useProductDetailEffect(product: DataLayerProduct | null, isRedeem?: boolean) {
    React.useEffect(() => {
      if (serverUtils.isServerSide()) {
        return;
      }
      if (product) {
        if (isRedeem) {
          window.dataLayer.push({
            event: EventTypes.productDetail,
            ecommerce: {
              detail: {
                actionField: { checkout: 'Redeem' },
                products: [product],
              },
            },
          });
        } else {
          window.dataLayer.push({
            event: EventTypes.productDetail,
            ecommerce: {
              detail: {
                products: [product],
              },
            },
          });
        }
      }
    }, [product, isRedeem]);
  },

  useAddToCartCallback(
    params: AddToCartParams | null,
    event: EventTypes.addToCart | EventTypes.addToCart2,
  ) {
    return React.useCallback(() => {
      if (params) {
        const { products, currencyCode, isRedeem } = params;
        if (isRedeem) {
          window.dataLayer.push({
            event,
            ecommerce: {
              add: {
                actionField: {
                  checkout: 'Redeem',
                },
                products,
              },
              currencyCode,
            },
          });
        } else {
          window.dataLayer.push({
            event,
            ecommerce: {
              add: {
                actionField: {
                },
                products,
              },
              currencyCode,
            },
          });
        }
      }
    }, [event, params]);
  },

  useRemoveFromCartCallback() {
    return React.useCallback((params: RemoveFromCartParams | null) => {
      if (params) {
        if (params.isRedeem) {
          const { products, currencyCode } = params;
          window.dataLayer.push({
            event: EventTypes.removeFromCart,
            ecommerce: {
              remove: {
                products,
              },
              currencyCode,
              actionField: {
                checkout: 'Redeem',
              },
            },
          });
        } else {
          const { products, currencyCode } = params;
          window.dataLayer.push({
            event: EventTypes.removeFromCart,
            ecommerce: {
              remove: {
                products,
              },
              currencyCode,
            },
          });
        }
      }
    }, []);
  },

  useCheckoutCallback(params: CheckoutParams | null) {
    return React.useCallback(() => {
      if (params) {
        const { products, paymentMethodId } = params;
        if (params.isRedeem) {
          window.dataLayer.push({
            event: EventTypes.checkout,
            ecommerce: {
              checkout: {
                actionField: { step: 1, option: paymentMethodId || '', checkout: 'Redeem' },
                products,
              },
            },
          });
        } else {
          window.dataLayer.push({
            event: EventTypes.checkout,
            ecommerce: {
              checkout: {
                actionField: { step: 1, option: paymentMethodId || '' },
                products,
              },
            },
          });
        }
      }
    }, [params]);
  },

  useSuccessfulOrderCallback(params: SuccessfulOrderParams | null, isActivation?: boolean) {
    return React.useCallback(() => {
      if (params) {
        const { products, isRedeem, ...rest } = params;
        if (isRedeem) {
          window.dataLayer.push({
            event: EventTypes.redeemOnlineOrder,
            ecommerce: {
              purchase: {
                actionField: {
                  checkout: 'Redeem',
                  affiliation: 'regular',
                  ...rest,
                },
                products,
              },
            },
          });
        } else {
          const { products, ...rest } = params;
          window.dataLayer.push({
            event: EventTypes.onlineOrder,
            ecommerce: {
              purchase: {
                actionField: {
                  affiliation: isActivation
                    ? AffiliationDataLayerParam.ACTIVATION
                    : AffiliationDataLayerParam.REGULAR,
                  ...rest,
                },
                products,
              },
            },
          });
        }
      }
    }, [isActivation, params]);
  },

  useLoginUserSuccessCallback() {
    return React.useCallback(() => {
      window.dataLayer.push({
        event: EventTypes.login,
        loginMethod: 'website',
      });
    }, []);
  },

  useDataLayerParams({
    order,
    redeemOrder,
    isRedeem,
  }: TrackingSuccessfulOrderArgs): SuccessfulOrderParams | null {
    const getMappedRedeemProducts =
      dataLayerMappingHooks.useMapRedeemOrderToDataLayerCheckoutProduct();
    const getMappedOrderProducts = dataLayerMappingHooks.useMapOrderToDataLayerCheckoutProduct();
    return React.useMemo(() => {
      if (
        (!order?.shipments || !order.totals)
        && (!redeemOrder?.shipments || !redeemOrder.totals)
      ) {
        return null;
      }

      let products: DataLayerCheckoutProduct[] = [];
      let revenue = '',
        shipping = '',
        tax = '';

      if (isRedeem && redeemOrder) {
        products = getMappedRedeemProducts(redeemOrder.shipments);
      } else if (order) {
        products = getMappedOrderProducts(order.shipments);
      }

      if (order && order.totals) {
        revenue = order.totals.grandTotal.value.toFixed(2);
        shipping = order.totals.adjustments
          .reduce((prev, cur) => {
            return cur.adjustmentType === AdjustmentType.Shipping ? prev + cur.price.value : prev;
          }, 0)
          .toFixed(2);
        tax = (order.totals.grandTotal.value - order.totals.grandTotalWithoutTax.value).toFixed(2);
      }
      if (redeemOrder && redeemOrder.totals) {
        revenue = redeemOrder.totals.grandTotal.value.toFixed(2);
        shipping = redeemOrder.totals.adjustments
          .reduce((prev, cur) => {
            return cur.adjustmentType === AdjustmentType.Shipping ? prev + cur.price.value : prev;
          }, 0)
          .toFixed(2);
        tax = (
          redeemOrder.totals.grandTotal.value - redeemOrder.totals.grandTotalWithoutTax.value
        ).toFixed(2);
      }

      return products.length > 0
        ? {
          id: redeemOrder?.id ?? order?.id ?? '',
          products,
          revenue,
          tax,
          shipping,
          isRedeem: !!isRedeem,
          billingEmail:
            order && order.__typename === 'GiftcardActivationOrder'
              ? order.customerEmail
              : order
                ? order.billingAddress?.email
                : redeemOrder?.billingAddress?.email,
        }
        : null;
    }, [order, redeemOrder, isRedeem, getMappedRedeemProducts, getMappedOrderProducts]);
  },

  useTrackingSuccessfulOrder({
    order,
    redeemOrder,
    isRedeem,
  }: TrackingSuccessfulOrderArgs) {
    const successfulOrderDataLayerParams = dataLayerHooks.useDataLayerParams({
      order,
      redeemOrder,
      isRedeem,
    });
    const trackSuccessfulOrder = dataLayerHooks.useSuccessfulOrderCallback(
      successfulOrderDataLayerParams,
    );

    React.useEffect(() => {
      if (order || redeemOrder) {
        trackSuccessfulOrder();
      }
    }, [order, trackSuccessfulOrder, redeemOrder]);
  },
};
